import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { promangToast as toast } from "../../../../operationFeedback/toastQueue";
import { fetchSjaStepTemplates } from "../../../../api/hse/risk";
import { riskApiError } from "./riskUtils";
import { applySjaStepTemplate } from "./sjaStepTemplates";

const selectClass =
  "w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#1463C7]";

export function SjaStepTemplatePicker({ disabled = false, onApply }) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSjaStepTemplates()
      .then((items) => {
        if (!cancelled) setTemplates(items || []);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleApply = async () => {
    const template = templates.find((item) => String(item.id) === String(selectedId));
    if (!template || disabled || applying) return;
    setApplying(true);
    try {
      if (!template.steps?.length) {
        toast.error(t("hse.sja.template.empty_steps"));
        return;
      }
      onApply?.((form) => applySjaStepTemplate(form, template));
      toast.success(t("hse.sja.template.applied"));
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.sja.template.load_failed"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-[14px] border border-slate-200 bg-slate-50/70 p-4">
      <p className="text-[13px] font-bold text-slate-700">{t("hse.sja.template.pick")}</p>
      <p className="mt-1 text-xs text-slate-500">{t("hse.sja.template.hint")}</p>
      {loading ? (
        <p className="mt-3 text-sm text-slate-500">{t("hse.sja.template.loading")}</p>
      ) : templates.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{t("hse.sja.template.empty")}</p>
      ) : (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <select
            aria-label={t("hse.sja.template.pick")}
            className={selectClass}
            value={selectedId}
            disabled={disabled || applying}
            onChange={(event) => setSelectedId(event.target.value)}
          >
            <option value="">{t("hse.sja.template.placeholder")}</option>
            {templates.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title || t("hse.sja.template.untitled")}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={disabled || !selectedId || applying}
            onClick={handleApply}
            className="shrink-0 rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {applying ? t("hse.sja.template.applying") : t("hse.sja.template.apply")}
          </button>
        </div>
      )}
    </div>
  );
}
